import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = user
  const [user, setUser] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(false);
      setSavedIds(new Set());
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      tokenStore.clear();
      setUser(false);
      setSavedIds(new Set());
    }
  }, []);

  const refreshSavedIds = useCallback(async () => {
    try {
      const { data } = await api.get("/saved/ids");
      setSavedIds(new Set(data.ids || []));
    } catch {/* ignore */}
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // When user becomes known, load saved IDs once.
  useEffect(() => {
    if (user && typeof user === "object") refreshSavedIds();
    else setSavedIds(new Set());
  }, [user, refreshSavedIds]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      tokenStore.set(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      tokenStore.set(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {/* ignore */}
    tokenStore.clear();
    setUser(false);
    setSavedIds(new Set());
  };

  const isSaved = (vid) => savedIds.has(vid);
  const markSaved = (vid, saved) => {
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (saved) next.add(vid);
      else next.delete(vid);
      return next;
    });
  };

  return (
    <AuthCtx.Provider value={{ user, login, register, logout, refresh, isSaved, markSaved, refreshSavedIds }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
